"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { 
  FaLaptopCode, 
  FaChartLine, 
  FaPenNib, 
  FaBuilding, 
  FaHandshake, 
  FaCheckCircle,
  FaArrowRight,
  FaUikit
} from "react-icons/fa";
import { HiOutlineBriefcase } from "react-icons/hi";

const Internship = () => {
  const sectionRef = useRef(null);
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      gsap.registerPlugin(ScrollTrigger);

      const ctx = gsap.context(() => {
        // Animate Left Column (Company Info)
        gsap.fromTo(leftColRef.current,
          { x: -50, opacity: 0 },
          {
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 75%",
            },
            x: 0,
            opacity: 1,
            duration: 1,
            ease: "power3.out"
          }
        );

        // Animate Right Column (Roles) - Staggered
        const roles = rightColRef.current?.querySelectorAll(".role-card");
        if (roles) {
          gsap.fromTo(roles,
            { y: 30, opacity: 0 },
            {
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 75%",
              },
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.15,
              ease: "back.out(1.5)"
            }
          );
        }
      }, sectionRef);

      return () => ctx.revert();
    }
  }, []);

  const roles = [
    { icon: <FaLaptopCode />, title: "Web Development", desc: "React, Next.js, Node.js" },
    { icon: <FaChartLine />, title: "Digital Marketing", desc: "SEO, SEM, Social Media" },
    { icon: <FaPenNib />, title: "Content Writing", desc: "Blogs, News, Copywriting" },
    { icon: <FaUikit />, title: "UI/UX Design", desc: "Figma, Miro" },
  ];

  return (
    <section ref={sectionRef} className="relative py-24 bg-slate-950 overflow-hidden font-sans">
      
      {/* --- Background Effects --- */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-emerald-900/10 rounded-full blur-[120px]"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        
        {/* --- Header --- */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wide mb-4">
            <HiOutlineBriefcase /> Career Launchpad
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight">
            Real World <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Experience</span>
          </h2>
          
          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
            Don't just learn it, do it. Complete any course at Vidyastu and unlock internship opportunities with one of our technology partners.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          {/* --- Left Column: Partner Company (5 Cols) --- */}
          <div ref={leftColRef} className="lg:col-span-5">
            <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-1 border border-white/10 shadow-2xl">
              {/* Hiring Badge */}
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg animate-bounce">
                Hiring Now
              </div>

              <div className="bg-slate-950/50 backdrop-blur-xl rounded-[22px] p-8 h-full flex flex-col items-center text-center">
                
                {/* LOGO SECTION - Replace src with actual logo */}
                <div className="w-32 h-32 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg p-4">
                  <Image 
                    src="/images/ags-logo.png" 
                    width={100} 
                    height={100} 
                    alt="Amrut Global Solutions Logo" 
                    className="object-contain"
                  />
                  {/* Fallback Icon if no image yet */}
                  <FaBuilding className="text-slate-800 text-5xl" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-2">
                  Amrut Global Solutions Private Limited
                </h3>

                <div className="w-full h-[1px] bg-white/10 mb-6"></div>

                <p className="text-slate-300 text-sm leading-relaxed mb-8">
                  A premier technology solutions provider specializing in secure-by-design web applications, digital transformation, and custom software development.
                </p>

                <div className="flex flex-col gap-3 w-full">
                  
                  <Link 
                    href="https://amrutglobalsolutions.com" // Update link if needed
                    target="_blank"
                    className="group flex items-center justify-center gap-2 p-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-bold transition-all"
                  >
                    Visit Website <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* --- Right Column: Roles & Benefits (7 Cols) --- */}
          <div ref={rightColRef} className="lg:col-span-7 space-y-8">
            
            {/* Guarantee Box */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6 flex items-start gap-4">
              <div className="bg-emerald-500/20 p-2 rounded-full text-emerald-400 mt-1">
                <FaCheckCircle size={20} />
              </div>
              <div>
                <h4 className="text-emerald-400 font-bold text-lg mb-1">Internship Program</h4>
                <p className="text-slate-300 text-sm">
                  Upon successful course completion, every student is eligible for a 3-6 month internship to work on live client projects.
                </p>
              </div>
            </div>

            {/* Roles Grid */}
            <div>
              <h4 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-blue-500 rounded-full"></span> Open Positions
              </h4>
              <div className="grid sm:grid-cols-2 gap-4">
                {roles.map((role, idx) => (
                  <div 
                    key={idx} 
                    className="role-card group p-5 bg-slate-900 border border-white/5 rounded-xl hover:border-blue-500/30 hover:bg-slate-800 transition-all duration-300 cursor-default"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-600/10 text-blue-400 flex items-center justify-center text-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                        {role.icon}
                      </div>
                      <h5 className="text-white font-bold">{role.title}</h5>
                    </div>
                    <p className="text-slate-500 text-xs pl-[3.5rem] group-hover:text-slate-400 transition-colors">
                      {role.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Info */}
            <div className="pt-4 border-t border-white/5">
              <p className="text-slate-400 text-sm text-center lg:text-left italic">
                * Performance-based stipends available for top performers.
              </p>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Internship;